/**
 * The cashflow & pension Statement of Advice, in the shared document's shape
 * (features/advice/moduleSoa.ts). Quotes the option's own snapshot and projections and the
 * change-nothing option's.
 */
import { moduleSoaCommon, replacementsFor, piecesForKinds, whyItFitsFrom, projectionRowsFrom, COMMON_IN_SCOPE, commonOutOfScope, type ModuleSoaSnapshot, type SoaMoneyRow, type SoaEstateDriver, type SoaProjectionRow } from '../advice/moduleSoa.ts';
import type { SoaComparisonRow } from '../advice/soaKit.ts';
import { riskKindsFromCandidateId, type RiskKind } from '../../strategy/strategyRisks.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { CashflowPensionResult, SupportCostInput } from './cashflowPensionScenario.ts';
import type { CashflowPensionCandidate } from './cashflowPensionCandidates.ts';
import { buildSoaGuaranteedIncome, type SoaGuaranteedIncome } from '../advice/soaGuaranteedIncome.ts';
import { familyPanelKindOf } from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { cpPlanWords, cpKindsOf, cpWhyLines } from './cpPlanWords.ts';
import { householdVoice } from '../../utils/householdWords.ts';

export interface CpSoaParams {
    option: CashflowPensionResult;
    all: CashflowPensionResult[];
    /** Which option led on each goal, as the page works it out: goal label → option id. */
    leaders: { label: string; id: string }[];
    factFind: DraftFactFind;
    legacyYears: number;
    supportCost: SupportCostInput | null;
    /**
     * The plan as it was run — the candidate the engine evaluated (or Manual Control's). Its fields
     * carry the amounts each lever moves; the result the page holds carries only the outcome.
     */
    candidate?: Pick<CashflowPensionCandidate, 'funeralBondPurchase' | 'giftingAmount' | 'superContribution' | 'investmentBond'> & { annuity?: Record<string, unknown> } | null;
    /** Every candidate as run, to tell which options carry a guaranteed income (for the best plan without one). */
    candidates?: Array<Pick<CashflowPensionCandidate, 'id' | 'annuity'>> | null;
    /**
     * A plan built by hand ("Build a plan yourself", cashflow rebuild 21 Oct 2026): the document says so, and
     * reads the plan's parts from what it does (its id, 'cp-manual', names no parts).
     */
    manual?: boolean;
    /** A hand-built plan customised from a tested one ("Customise this plan", 25 Oct 2026): that plan's title. */
    customisedFrom?: string | null;
}

/*
 * THE GUARANTEED INCOME SECTION (owner, 21 Oct 2026 — the guaranteed income review R1–R4, mock-up screen 5): for
 * an option with a guaranteed income, the words for its type, the income and where it came from, the family
 * table year by year against changing nothing (every such option here buys it and does nothing else), the best
 * option tested without one, the assumptions, the client's dated answers and, for income only, the
 * confirmation word for word. The shared section (features/advice/soaGuaranteedIncome.ts).
 */
function cpGuaranteedIncome(p: CpSoaParams, nothing: CashflowPensionResult | null, years: number): SoaGuaranteedIncome | null {
    const a = p.candidate?.annuity as { purchaseAmount?: unknown; annuityType?: unknown; residualCapital?: unknown; ownership?: unknown; incomeOverride?: unknown } | undefined;
    const amount = n(a?.purchaseAmount);
    if (!a || !(amount > 0)) return null;
    const owner = a.ownership === 'partner' ? 'partner' : 'client';
    const personName = (owner === 'partner' ? p.factFind?.partner?.firstName : p.factFind?.client?.firstName) || (owner === 'partner' ? 'the partner' : 'the client');
    const withAnnuity = new Set((p.candidates || []).filter((c) => c.annuity).map((c) => c.id));
    const without = p.all.filter((s) => !s.error && !withAnnuity.has(s.id) && !(p.candidates == null && /annuity/i.test(String(s.id))));
    const best = without.reduce<CashflowPensionResult | null>((b, s) => (!b || n(s.snapshot?.estate) > n(b.snapshot?.estate) ? s : b), null);
    // The best plan without one, in the screen's words (cpPlanWords); changing nothing keeps its own name.
    const altName = (s: CashflowPensionResult) => {
        const c = (p.candidates || []).find((x) => x.id === s.id);
        return s.changeNothing || !c ? s.name : cpPlanWords(c as never, p.factFind).title;
    };
    return buildSoaGuaranteedIncome({
        factFind: p.factFind,
        personName,
        amount,
        kind: familyPanelKindOf({ annuityType: a.annuityType, residualCapital: a.residualCapital }),
        incomeAYear: n((p.option.annuityTerms as { annualPayment?: unknown } | null | undefined)?.annualPayment) || null,
        quoted: n(a.incomeOverride) > 0,
        rows: p.option.projections,
        compareRows: nothing?.projections ?? null,
        compareNote: nothing ? 'the same plan without it, the money left in savings' : null,
        horizonYears: years,
        plan: { estate: n(p.option.snapshot?.estate), pension: n(p.option.snapshot?.pension), cashflow: n(p.option.snapshot?.cashflow) },
        alternative: best ? { name: altName(best), estate: n(best.snapshot?.estate), pension: n(best.snapshot?.pension), cashflow: n(best.snapshot?.cashflow) } : null,
    });
}

const n = (v: unknown): number => {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(x) ? x : 0;
};
const money = (v: number): string => `$${Math.round(v).toLocaleString('en-AU')}`;

export function buildCashflowPensionSoaSnapshot(p: CpSoaParams): ModuleSoaSnapshot {
    const { option, factFind } = p;
    const id = String(option.id || '');
    /*
     * The plan's parts from what it does when the candidate is known (a hand-built plan's id names none), else
     * from its id as before. And its words are the screen's own (cpPlanWords): the card, the plan page and the
     * document say the same thing (cashflow rebuild, 21 Oct 2026).
     */
    const kinds = p.candidate ? cpKindsOf({ id, ...(p.candidate as object) }) : riskKindsFromCandidateId(id);
    const candidateOf = (s: CashflowPensionResult) => (s.id === option.id && p.candidate ? { id, ...(p.candidate as object) } : (p.candidates || []).find((c) => c.id === s.id) ?? null);
    const wordsOf = (s: CashflowPensionResult) => {
        const c = candidateOf(s);
        return s.changeNothing ? { title: 'Changing nothing', sub: s.description } : c ? cpPlanWords(c as never, factFind) : { title: s.name, sub: s.description };
    };
    const words = wordsOf(option);
    const years = Number(p.legacyYears) || 10;
    const nothing = p.all.find((s) => s.changeNothing) || null;
    const headline = { pension: n(option.snapshot?.pension), cashflow: n(option.snapshot?.cashflow), estate: n(option.snapshot?.estate) };
    const baseline = { name: 'Changing nothing', pension: n(nothing?.snapshot?.pension), cashflow: n(nothing?.snapshot?.cashflow), estate: n(nothing?.snapshot?.estate) };
    const terms = (option.annuityTerms || {}) as { purchaseAmount?: number };
    const annuityAmount = n(terms.purchaseAmount);

    const common = moduleSoaCommon(factFind, { kind: 'cashflow_pension', strategySource: p.manual ? 'manual_control' : 'suggestion_engine', customisedFrom: p.customisedFrom ?? null, riskKinds: kinds });

    /*
     * The amounts come from the plan AS RUN — the candidate's own fields — never from its wording.
     * The first cut read them out of the description with patterns, and the descriptions carry no
     * figures for the funeral bond or the gift ("up to the exempt limit", "the yearly free area"), so
     * both were silently left out of the document; and the investment bond's description mentions
     * super ("the tax-paid home for money super can no longer take"), so the bond was listed twice,
     * once as a super contribution (review of Joe and Kim Smith's file, 18 Oct 2026).
     *
     * Each lever is read from its one field, and only where the option's kinds include it, so an
     * investment bond is classified once, as a bond.
     */
    const lever = p.candidate || {};
    const has = (kind: RiskKind) => kinds.includes(kind);
    const superTotal = n(lever.superContribution?.client) + n(lever.superContribution?.partner);
    const amounts: Partial<Record<RiskKind, number>> = {
        annuityNil: annuityAmount, annuityReturned: annuityAmount, annuityGuaranteed: annuityAmount,
        funeralBond: n(lever.funeralBondPurchase),
        gift: n(lever.giftingAmount),
        superContribution: has('investmentBond') ? 0 : superTotal,
        investmentBond: n(lever.investmentBond),
    };
    const moneyFlow: SoaMoneyRow[] = [
        ...(annuityAmount > 0 ? [{ label: 'Guaranteed income for life (a lifetime annuity)', amount: annuityAmount }] : []),
        ...(amounts.funeralBond ? [{ label: 'Funeral bond', amount: amounts.funeralBond }] : []),
        ...(amounts.gift ? [{ label: 'Gift to family', amount: amounts.gift }] : []),
        ...(amounts.superContribution ? [{ label: 'Into super', amount: amounts.superContribution }] : []),
        ...(amounts.investmentBond ? [{ label: 'Investment bond', amount: amounts.investmentBond }] : []),
    ];
    const pieces = piecesForKinds(kinds, amounts);
    // The why on the three levers, in the screen's words (cpWhyLines), then the plain comparison.
    const why = nothing ? cpWhyLines(headline, baseline, candidateOf(option) as never, years, householdVoice(factFind), n((option.annuityTerms as { annualPayment?: unknown } | null | undefined)?.annualPayment) || null) : [];
    const inBrief = [words.sub || words.title, ...(why.length ? why : whyItFitsFrom(headline, baseline, years))].filter(Boolean);

    const alternatives = p.all
        .filter((s) => !s.error && (p.leaders.some((l) => l.id === s.id) || s.id === option.id))
        .map((s) => ({
            name: wordsOf(s).title,
            description: wordsOf(s).sub,
            pension: n(s.snapshot?.pension),
            cashflow: n(s.snapshot?.cashflow),
            estate: n(s.snapshot?.estate),
            selected: s.id === option.id,
            badges: p.leaders.filter((l) => l.id === s.id).map((l) => l.label),
        }));

    const support = p.supportCost;
    const allEvaluated: SoaComparisonRow[] = p.all
        .filter((s) => !s.error && !s.changeNothing)
        .map((s) => ({ name: wordsOf(s).title, description: wordsOf(s).sub, pension: n(s.snapshot?.pension), cashflow: n(s.snapshot?.cashflow), estate: n(s.snapshot?.estate), selected: s.id === option.id, badges: p.leaders.filter((l) => l.id === s.id).map((l) => l.label) }));
    const projRows = (list: readonly unknown[] | undefined): SoaProjectionRow[] =>
        projectionRowsFrom(list, years, support ? { moduleCostLabel: support.label, moduleCost: () => support.annual } : {});
    const pensionDelta = Math.round(headline.pension - baseline.pension);
    const estateDrivers: SoaEstateDriver[] = [
        ...(amounts.gift ? [{ text: 'The gift leaves the estate on the day; it is in the family\'s hands now rather than later', amount: amounts.gift, sign: 'minus' as const }] : []),
        ...(kinds.includes('annuityNil') && annuityAmount > 0 ? [{ text: 'A nil-residual annuity leaves nothing of its price to the estate; its value is the income for life', amount: annuityAmount, sign: 'minus' as const }] : []),
        ...(kinds.includes('annuityReturned') && annuityAmount > 0 ? [{ text: 'The annuity\'s withdrawal value winds down on a set schedule; what is left at the horizon stays in the estate', amount: annuityAmount, sign: 'neutral' as const }] : []),
        ...(amounts.funeralBond ? [{ text: 'The funeral bond is set aside for the funeral, so it is not counted and not part of what is left to spend', amount: amounts.funeralBond, sign: 'neutral' as const }] : []),
        ...(pensionDelta !== 0 ? [{ text: `${pensionDelta > 0 ? 'More' : 'Less'} Age Pension, compounding over the ${years} years`, amount: Math.abs(pensionDelta), perYear: true, sign: (pensionDelta > 0 ? 'plus' : 'minus') as 'plus' | 'minus' }] : []),
        ...(headline.cashflow !== baseline.cashflow ? [{ text: `${headline.cashflow > baseline.cashflow ? 'Less' : 'More'} drawn from savings each year to cover the bills`, amount: Math.abs(Math.round(headline.cashflow - baseline.cashflow)), perYear: true, sign: (headline.cashflow > baseline.cashflow ? 'plus' : 'minus') as 'plus' | 'minus' }] : []),
    ];
    const motiveNote = headline.estate < baseline.estate - 1
        ? `An estate that ends behind ${baseline.name.toLowerCase()} is the price of the income and the pension this plan buys${support ? `, with ${money(support.annual)} a year of ${support.label.toLowerCase()} to pay for` : ''}. The trade is money you can rely on now against money left later; the figures show the size of it, so the decision is made with eyes open.`
        : null;
    return {
        ...common,
        decisionIntro: `You asked how to make your money go further at home${support ? `, with ${money(support.annual)} a year of ${support.label.toLowerCase()} to pay for` : ''}: lifting the Age Pension, covering the bills each year, and keeping as much as possible for the family. This advice is about the levers that do that, and what each one costs.`,
        selectedStrategyName: words.title,
        selectedStrategyDescription: words.sub,
        inOneLine: words.sub || words.title,
        inBrief,
        pieces,
        moneyFlow,
        legacyYears: years,
        headline,
        baseline,
        whyItFits: whyItFitsFrom(headline, baseline, years),
        estateDrivers,
        motiveNote,
        modulePage: support ? {
            title: 'Your spending',
            intro: 'The plan has to cover these each year. Where it does not, the projections draw the shortfall from savings.',
            rows: [
                { label: 'Living expenses', amount: common.annualLivingExpenses, note: 'a year, as you entered them' },
                { label: support.label, amount: support.annual, note: 'a year' },
            ],
        } : null,
        moduleCostLabel: support ? support.label : '',
        projectionConvention: 'Year 1 is the first full year after the change; each later year follows it.',
        alternatives,
        allEvaluated,
        totalEvaluated: allEvaluated.length,
        projections: projRows(option.projections),
        baselineProjections: projRows(nothing?.projections),
        replacements: replacementsFor(kinds, moneyFlow),
        implications: [
            ...(annuityAmount > 0 ? [{ title: 'The annuity needs a provider quote', lead: `The ${money(annuityAmount)} annuity is priced on a published rate sheet.`, body: 'The income shown is an estimate. Get a quote from the insurer before buying; if it differs, re-run the analysis on the real figure.' }] : []),
            ...(amounts.gift ? [{ title: 'Gifts have a limit', lead: 'Up to $10,000 a year and $30,000 over five years stops being counted.', body: 'Tell Services Australia about the gift. Anything above the limit still counts as your asset, and is deemed, for five years.' }] : []),
            { title: 'Tell Services Australia', lead: 'Each of these moves changes what is counted.', body: 'Report the change within 14 days so the pension is reassessed on the new figures.' },
        ],
        scope: {
            inScope: ['The levers that lift the Age Pension or free up cashflow while you stay at home, using the figures you entered.', ...COMMON_IN_SCOPE],
            outOfScope: ['Moving house, a village, aged care or a granny flat — each has its own module and its own advice.', ...commonOutOfScope(factFind)],
        },
        nextSteps: [
            ...(annuityAmount > 0 ? ['Get a quote from an annuity provider and re-run the analysis on it.'] : []),
            ...(amounts.funeralBond ? ['Compare funeral bond providers on fees; the exemption limit is the same whichever you choose.'] : []),
            ...(amounts.superContribution ? ['Check with your super fund that the contribution can be accepted at your age and balance.'] : []),
            'Tell Services Australia about each change within 14 days.',
            'Where you sell assets or trigger capital gains tax, confirm the tax with a registered tax agent before you act.',
        ],
        guaranteedIncome: cpGuaranteedIncome(p, nothing, years),
    };
}
